"use client"

import { ArbreAddressUpdateInput, ArbreCoordinateUpdateInput, ArbreCreateInput, ArbreListResponse, ArbreResponse, ArbreSelectInput, ArbreUpdateInput } from '@repo/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useQueryWithConfig } from '../../../hooks/api/useQueryWithConfig';
import { services } from '../../../lib/api/services';

// Hooks for fetching arbres
export const useArbres = (filters: ArbreSelectInput) => {
  const { page = 1, limit = 10, ...filterParams } = filters;

  // Handle search terms efficiently - prepare search params based on what's available
  const searchParams = Object.fromEntries(
    Object.entries(filterParams).filter(([_, value]) => value !== undefined && value !== null && value !== '')
  );

  const keys = [
    "arbres",
    "list",
    page,
    limit,
    Object.keys(searchParams).length > 0 ? JSON.stringify(searchParams) : null
  ].filter(Boolean);

  return useQueryWithConfig<ArbreListResponse>(
    keys as string[],
    () => services.arbre.getArbres({
      page,
      limit,
      includeDeleted: false,
      ...searchParams
    }),
    {
      // Avoid unnecessary refetches when search parameters haven't actually changed
      staleTime: 1000 * 60, // 1 minute
    }
  );
};

export const useArbre = (id: number) =>
  useQueryWithConfig<ArbreResponse>(
    ['arbre', id],
    () => services.arbre.getArbreById(id),
    {
      enabled: !!id,
    }
  );

// Mutation hook for creating arbres
export function useCreateArbre() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ArbreCreateInput) => services.arbre.createArbre(data),
    onSuccess: () => {
      // Invalidate and refetch arbres list
      queryClient.invalidateQueries({ queryKey: ['arbres'] });
    },
  });
}

// Mutation hook for updating arbres
export function useUpdateArbre() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ArbreUpdateInput }) =>
      services.arbre.updateArbre(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate and refetch arbres list and specific arbre
      queryClient.invalidateQueries({ queryKey: ['arbres'] });
      queryClient.invalidateQueries({ queryKey: ['arbre', id] });
    },
  });
}

// Mutation hook for updating arbre coordinates
export function useUpdateArbreCoordinates() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ArbreCoordinateUpdateInput }) =>
      services.arbre.updateCoordinates(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate and refetch arbres list and specific arbre
      queryClient.invalidateQueries({ queryKey: ['arbres'] });
      queryClient.invalidateQueries({ queryKey: ['arbre', id] });
      // Also invalidate inventory queries that might include this arbre
      queryClient.invalidateQueries({ queryKey: ['inventaire'] });
    },
    onError: (error) => {
      console.error('Error updating arbre coordinates:', error);
    }
  });
}

// Mutation hook for updating arbre address and coordinates
export function useUpdateArbreAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ArbreAddressUpdateInput }) =>
      services.arbre.updateAddress(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate and refetch arbres list and specific arbre
      queryClient.invalidateQueries({ queryKey: ['arbres'] });
      queryClient.invalidateQueries({ queryKey: ['arbre', id] });
      // Also invalidate inventory queries that might include this arbre
      queryClient.invalidateQueries({ queryKey: ['inventaire'] });
    },
    onError: (error) => {
      console.error('Error updating arbre address:', error);
    }
  });
}

// Mutation hook for deleting arbres
export function useDeleteArbre() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => services.arbre.deleteArbre(id),
    onSuccess: (_, id) => {
      // Invalidate and refetch arbres list and remove specific arbre from cache
      queryClient.invalidateQueries({ queryKey: ['arbres'] });
      queryClient.removeQueries({ queryKey: ['arbre', id] });
    },
  });
}

// Mutation hook for updating arbre images
export function useUpdateArbreImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      field,
      file
    }: {
      id: number;
      field: 'img1' | 'img2' | 'img3';
      file: File
    }) => services.arbre.updateImage(id, field, file),
    onSuccess: (_, { id }) => {
      // Invalidate and refetch arbres list and specific arbre
      queryClient.invalidateQueries({ queryKey: ['arbres'] });
      queryClient.invalidateQueries({ queryKey: ['arbre', id] });
      // Also invalidate inventory queries that might include this arbre
      queryClient.invalidateQueries({ queryKey: ['inventaire'] });
    },
    onError: (error) => {
      console.error('Error updating arbre image:', error);
    }
  });
}

// Mutation hook for deleting arbre images
export function useDeleteArbreImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      field
    }: {
      id: number;
      field: 'img1' | 'img2' | 'img3'
    }) => services.arbre.deleteImage(id, field),
    onSuccess: (_, { id }) => {
      // Invalidate and refetch arbres list and specific arbre
      queryClient.invalidateQueries({ queryKey: ['arbres'] });
      queryClient.invalidateQueries({ queryKey: ['arbre', id] });
      // Also invalidate inventory queries that might include this arbre
      queryClient.invalidateQueries({ queryKey: ['inventaire'] });
    },
    onError: (error) => {
      console.error('Error deleting arbre image:', error);
    }
  });
}

// Mutation hook for updating arbre status travaux
export function useUpdateArbreStatusTravaux() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ids, status_travaux }: { ids: number[]; status_travaux: boolean }) =>
      services.arbre.updateStatusTravaux(ids, status_travaux),
    onSuccess: (_, { ids }) => {
      // Invalidate and refetch arbres list and specific arbres
      queryClient.invalidateQueries({ queryKey: ['arbres'] });
      ids.forEach(id => {
        queryClient.invalidateQueries({ queryKey: ['arbre', id] });
      });
      // Also invalidate inventory queries that might include these arbres
      queryClient.invalidateQueries({ queryKey: ['inventaire'] });
    },
    onError: (error) => {
      console.error('Error updating arbre status travaux:', error);
    }
  });
}
